/**
 * FEAT-080 — is this the piece's own live URL, or something it merely cited?
 *
 * Amplification posts have been linking to sources quoted *inside* the article
 * rather than to the article, because `social-amplifier.ts` asks the model to
 * "Include article URL" without supplying one. The anchor that settles it
 * already exists: `publish_targets.site_url`, populated for every target that
 * matters. A candidate URL is the piece's own if it sits under the site its
 * target publishes to.
 *
 * Shared by the API and the SPA — one definition, the same way
 * `voice-scores.ts` is — because the server stays permissive and the hard block
 * is client-side, and two implementations of "does this match" would eventually
 * disagree about which URLs the founder is allowed to type.
 *
 * Deliberately a prefix match and nothing more. The spec names URL parsing as a
 * rabbit hole: no hostname decomposition, no query-string handling, no library.
 */

/**
 * The Publisher records a failed dispatch as `error://<message>` rather than
 * leaving the column empty. A deliberate convention, not a bug — `PublishedPanel`
 * already renders it as a known state — but it is never a link.
 */
const ERROR_SENTINEL = "error://";

/**
 * Trailing slashes are noise here: `https://x.com` and `https://x.com/` are one
 * site. Lowercased too — scheme and host are case-insensitive, and a URL pasted
 * from a source that upcases the host would otherwise be rejected as "not a
 * <site> link" with nothing on screen explaining why.
 *
 * This does lowercase the path as well, which is technically case-sensitive.
 * Harmless for a prefix check against an origin: it can only make the compare
 * more permissive about case, never about which site the URL belongs to.
 */
function normalise(url: string): string {
  return url.trim().toLowerCase().replace(/\/+$/, "");
}

/**
 * Does `externalUrl` live under `siteUrl`?
 *
 * Returns **`null`** when the target has no `site_url` configured — "no check
 * was possible" is a third answer and must not collapse into `false`. Some
 * target types genuinely have no site (a `docx` file has nowhere to live), and
 * reporting those as mismatched would mark every one of them wrong.
 */
export function matchesSiteUrl(
  externalUrl: string | null | undefined,
  siteUrl: string | null | undefined
): boolean | null {
  const site = siteUrl == null ? "" : normalise(siteUrl);
  if (!site) return null;

  const url = externalUrl == null ? "" : normalise(externalUrl);
  if (!url) return false;

  if (url === site) return true;
  // The separator matters: without it `https://b2x-marketing.com.evil.test`
  // would pass as a match for `https://b2x-marketing.com`.
  if (url.startsWith(`${site}/`)) return true;

  // A LinkedIn newsletter's site is its /newsletters/ page, but each issue is
  // served from /pulse/<slug> — never under that page (piece 1820, 2026-10-19).
  if (site.startsWith(LINKEDIN_NEWSLETTERS)) return url.startsWith(LINKEDIN_PULSE);
  return false;
}

const LINKEDIN_NEWSLETTERS = "https://www.linkedin.com/newsletters/";
const LINKEDIN_PULSE = "https://www.linkedin.com/pulse/";

/**
 * Can this result stand as the piece's canonical live URL?
 *
 * Note `matchesSiteUrl(...) !== false` rather than `=== true`: a target with no
 * configured site is unvalidatable, not disqualified. The alternative would
 * leave those pieces permanently without a primary and therefore permanently
 * without a link in their amplification.
 */
export function isEligibleAsPrimary(
  status: string | null | undefined,
  externalUrl: string | null | undefined,
  siteUrl: string | null | undefined
): boolean {
  if (status !== "published") return false;
  const url = externalUrl == null ? "" : externalUrl.trim();
  if (!url) return false;
  if (url.startsWith(ERROR_SENTINEL)) return false;
  return matchesSiteUrl(url, siteUrl) !== false;
}

/** True for the `error://<message>` rows the Publisher writes on a failed dispatch. */
export function isErrorUrl(externalUrl: string | null | undefined): boolean {
  return (externalUrl ?? "").startsWith(ERROR_SENTINEL);
}
